/*
 * What the search screen remembers: the term it is holding for this tab, and the handful of searches
 * this device has run.
 *
 * The term is not a route parameter and never becomes one (spec 10, A26): a query in a URL reaches
 * the browser history, the Referer header a click on a result's outbound link carries, and every
 * access log in front of the instance. What a person still expects — leaving a result list for a
 * conversation and coming back to it, or asking another surface to search inside a channel — is a
 * hand-over between two screens of the same tab, and that is what `takePendingSearch` is.
 *
 * The hand-over is `sessionStorage` and not a module variable so that a reload of the tab keeps the
 * term, and `sessionStorage` and not `localStorage` so that it dies with the tab rather than sitting
 * on the device. A read consumes it: a term that has been handed over has been used, and a stale
 * hand-over must not re-run a search behind somebody who cleared the box.
 *
 * *Recent searches* is the opposite decision for the opposite reason (VISUAL.md §7.10): it is a
 * convenience of the device and has to survive the tab, so it is `localStorage` — under the `tb:`
 * prefix, which is what `forgetAll()` sweeps at sign-out, so the searches of one person never greet
 * the next one on a shared machine.
 */

import {
	readJSON,
	readSessionValue,
	storageKey,
	writeJSON,
	writeSessionValue,
} from "../../lib/storage.ts"

/**
 * Where the term lives. The hand-over is not built with `storageKey()`: that helper's closed `Kind`
 * union is the list of what teamBase remembers *per device*, and the hand-over is the tab's own
 * scratch value, like the protocol reload guard.
 */
const KEY = "tb.search.q"

/** Beside the term: the one half of the screen the hand-over asks for, when it asks for one. */
const HALF_KEY = "tb.search.half"

/** The halves a hand-over may narrow the screen to: the drive's own search field asks for its own. */
export type PendingHalf = "drive"

/** The device's own list, a `Kind` of its own, under the prefix the sign-out sweep removes. */
const RECENT_KEY = storageKey("recentSearches")

/** How many searches are offered back. Five is a screenful of chips and a week of one person's use. */
const RECENT_MAX = 5

/**
 * The term another surface handed over — the search screen's own last search, or the query a
 * shortcut prepared. Consumed: a second read without a new hand-over answers nothing.
 */
export function takePendingSearch(): string | undefined {
	const term = readSessionValue(KEY)
	if (term === "") return undefined
	writeSessionValue(KEY, "")
	return term
}

/**
 * Hands a term to the search screen: the tab's restore value, and what a shortcut prepares. `half`
 * narrows the screen to one section — *Drive only*, from the drive's search field (S-01 §3.15).
 */
export function setPendingSearch(term: string, half?: PendingHalf): void {
	writeSessionValue(KEY, term)
	writeSessionValue(HALF_KEY, half ?? "")
}

/** The half the hand-over asked for, consumed with it; undefined for the whole screen. */
export function takePendingHalf(): PendingHalf | undefined {
	const half = readSessionValue(HALF_KEY)
	writeSessionValue(HALF_KEY, "")
	return half === "drive" ? half : undefined
}

/**
 * The searches this device ran, newest first. Anything the store holds that is not a list of strings
 * is ignored rather than repaired: a value written by another build is not this build's to read.
 */
export function recentSearches(): string[] {
	const stored = readJSON<unknown>(RECENT_KEY)
	if (!Array.isArray(stored)) return []
	return stored
		.filter((term): term is string => typeof term === "string" && term !== "")
		.slice(0, RECENT_MAX)
}

/** Puts one search at the top of that list, without a second copy of it further down. */
export function rememberSearch(term: string): void {
	const query = term.trim()
	if (query === "") return
	const kept = [query, ...recentSearches().filter((each) => each !== query)].slice(0, RECENT_MAX)
	writeJSON(RECENT_KEY, kept)
}
